import { fetchMetrics } from "../thunks/MetricDataDispatcher";
import { WIDGET_SCROLL_COMPLETED } from "../thunks/DashboardConfigurationDispatcher";
/**
 * Find the next available position in the grid for a new widget
 * @param {Array} existingLayouts - Current widget layouts with their positions
 * @param {Object} widgetSize - Desired widget dimensions {w, h}
 * @param {Object} gridConfig - Grid configuration {cols}
 * @returns {Object} Available position {x, y}
 */
const findNextAvailablePosition = (existingLayouts, widgetSize, gridConfig) => {
  const { w: widgetWidth, h: widgetHeight } = widgetSize;
  const { cols } = gridConfig;

  // Empty grid case
  if (!existingLayouts || !existingLayouts.length) {
    return { x: 0, y: 0 };
  }

  // Create a grid representation for efficient space checking
  const grid = {};

  // Fill grid with existing layouts
  existingLayouts.forEach((layout) => {
    const { x, y, w, h } = layout;

    // Mark all cells occupied by this layout
    for (let row = y; row < y + h; row++) {
      for (let col = x; col < x + w; col++) {
        if (!grid[row]) grid[row] = {};
        grid[row][col] = true;
      }
    }
  });

  // Find first available position that can fit the widget
  const maxRow = Math.max(
    ...existingLayouts.map((layout) => layout.y + layout.h),
    0,
  );

  // Check each row
  for (let y = 0; y <= maxRow + 1; y++) {
    // Check each possible x position in this row
    for (let x = 0; x <= cols - widgetWidth; x++) {
      let canFit = true;

      // Check if widget fits at this position
      for (let dy = 0; dy < widgetHeight; dy++) {
        for (let dx = 0; dx < widgetWidth; dx++) {
          if (grid[y + dy] && grid[y + dy][x + dx]) {
            canFit = false;
            break;
          }
        }
        if (!canFit) break;
      }

      // If widget fits, return this position
      if (canFit) {
        return { x, y };
      }
    }
  }

  // Fallback: place at bottom of grid
  return { x: 0, y: maxRow + 1 };
};

const getWidgetDimensions = (widget, isPriorityDashboard = false) => {
  // Use conditional checks for property access
  const size =
    (widget && widget.widgetDefinition && widget.widgetDefinition.size) || {};
  const savedSize = (widget && widget.size) || {};

  // Extract size properties with defaults using destructuring
  const {
    defaultWidth = 4,
    defaultHeight = 4,
    minWidth = 4,
    minHeight = 4,
    maxWidth = 12,
    maxHeight = 8,
  } = size;

  // Base dimensions common to both dashboard types
  const baseDimensions = {
    x: savedSize.x,
    y: savedSize.y,
    minHeight, // For mobile stacking
  };

  // Return appropriate dimensions based on dashboard type
  return isPriorityDashboard
    ? {
        ...baseDimensions,
        w: defaultWidth,
        h: defaultHeight,
        minW: defaultWidth,
        minH: defaultHeight,
        maxW: defaultWidth,
        maxH: defaultHeight,
      }
    : {
        ...baseDimensions,
        w: Math.max(savedSize.w || defaultWidth, minWidth),
        h: Math.max(savedSize.h || defaultHeight, minHeight),
        minW: minWidth,
        minH: minHeight,
        maxW: maxWidth,
        maxH: maxHeight,
      };
};

const createLayoutItem = (widget, i, x, y, w, h, minW, minH, maxW, maxH) => ({
  i: widget.widgetId || `widget-${i}`,
  x,
  y,
  w,
  h,
  minW,
  minH,
  maxW,
  maxH,
});

/**
 * Generate layouts based on widget configuration
 * @param {Object} dashboardConfig - Dashboard configuration object
 * @param {boolean} isPriorityDashboard - Whether this is a priority dashboard
 * @returns {Object} Responsive layout configuration for react-grid-layout
 */
export const generateLayouts = (
  dashboardConfig,
  isPriorityDashboard = false,
) => {
  if (
    !dashboardConfig ||
    !dashboardConfig.widgets ||
    !dashboardConfig.widgets.length
  ) {
    return null;
  }

  // Generate base layout for 12-column breakpoints (xl, lg, md)
  const baseLayouts = isPriorityDashboard
    ? generatePriorityDashboardLayouts(dashboardConfig)
    : generateRegularDashboardLayouts(dashboardConfig);

  // Create responsive layouts for different screen sizes
  return createResponsiveLayouts(
    baseLayouts,
    dashboardConfig,
    isPriorityDashboard,
  );
};

const generateRegularDashboardLayouts = (dashboardConfig) => {
  // Create a cache of existing layouts to avoid recalculating for each widget
  const layoutCache = [];

  return dashboardConfig.widgets.map((widget, i) => {
    const { w, h, x, y, minW, minH, maxW, maxH } = getWidgetDimensions(
      widget,
      false,
    );

    // Use saved position when available
    if (x !== undefined && y !== undefined) {
      // Add to layout cache for future widgets
      layoutCache.push({ x, y, w, h });
      return createLayoutItem(widget, i, x, y, w, h, minW, minH, maxW, maxH);
    }

    // Find available position for new widget
    const { x: newX, y: newY } = findNextAvailablePosition(
      layoutCache,
      { w, h },
      { cols: 12 },
    );

    // Add new position to layout cache for future widgets
    layoutCache.push({ x: newX, y: newY, w, h });

    return createLayoutItem(
      widget,
      i,
      newX,
      newY,
      w,
      h,
      minW,
      minH,
      maxW,
      maxH,
    );
  });
};

const createResponsiveLayouts = (
  baseLayouts,
  dashboardConfig,
  isPriorityDashboard,
) => {
  return {
    // 12-column layouts (xl, lg, md) - Widescreen, Desktop, Tablet landscape
    lg: baseLayouts,

    // 8-column layouts (sm, xs) - Tablet, Mobile large landscape
    md: baseLayouts.map((item) => ({
      ...item,
      w: Math.min(Math.ceil((item.w * 8) / 12), 8),
      x: Math.floor((item.x * 8) / 12),
      minW: Math.min(Math.ceil((item.minW * 8) / 12), 8),
      maxW: Math.min(Math.ceil((item.maxW * 8) / 12), 8),
    })),

    // 4-column layouts (xxs, xxxs, xxxxs) - Mobile landscape, Mobile large, Mobile
    // Stack widgets vertically, full width using minHeight from config
    sm: (() => {
      let currentY = 0;
      return baseLayouts.map((item, index) => {
        const widget = dashboardConfig.widgets[index];
        const { minHeight } = getWidgetDimensions(widget, isPriorityDashboard);
        const layoutItem = {
          ...item,
          w: 4, // Full width
          x: 0, // Left aligned
          y: currentY, // Stacked vertically using accumulated height
          h: minHeight, // Use minHeight for mobile stacking
        };
        currentY += minHeight;
        return layoutItem;
      });
    })(),
  };
};

/**
 * Generate layouts specifically for priority dashboards using first-fit approach
 * @param {Object} dashboardConfig - Dashboard configuration object
 * @returns {Array} Layout items for priority dashboard
 */
const generatePriorityDashboardLayouts = (dashboardConfig) => {
  if (
    !dashboardConfig ||
    !dashboardConfig.widgets ||
    !dashboardConfig.widgets.length
  ) {
    return [];
  }

  const COLS = 12;
  const rows = []; // Each row tracks its height and used width

  return dashboardConfig.widgets.map((widget, i) => {
    const { w, h, minW, minH, maxW, maxH } = getWidgetDimensions(widget, true);

    let rowIndex;
    let position;

    // Check if there's a last row and if the widget fits there
    if (rows.length > 0) {
      const lastRow = rows[rows.length - 1];
      const remainingWidth = COLS - lastRow.usedWidth;

      // If widget fits in the last row, use it (first-fit approach)
      if (remainingWidth >= w) {
        rowIndex = rows.length - 1;
        position = lastRow.usedWidth;
      } else {
        // Widget doesn't fit in last row, create a new one
        rowIndex = rows.length;
        position = 0;
        rows.push({
          usedWidth: 0,
          height: h,
          y: rows.reduce((sum, row) => sum + row.height, 0),
        });
      }
    } else {
      // No rows yet, create the first one
      rowIndex = 0;
      position = 0;
      rows.push({
        usedWidth: 0,
        height: h,
        y: 0,
      });
    }

    // Update row usage
    rows[rowIndex].usedWidth += w;

    // Create layout item
    return createLayoutItem(
      widget,
      i,
      position,
      rows[rowIndex].y,
      w,
      h,
      minW,
      minH,
      maxW,
      maxH,
    );
  });
};

export const buildMetricFilters = (stationCode, cycles) => {
  return [
    {
      name: "STATION_CODE",
      value: stationCode,
    },
    ...cycles.map((cycle) => ({
      name: "CYCLE_ID",
      value: cycle,
    })),
  ];
};

export const getWCCSSafeCapability = async () => {
  const { getNodeCapability } = await import(
    "../../../handler/ParentDataHandler"
  );
  const result = await getNodeCapability();
  return result.capability.replaceAll("_", "").replaceAll('"', "");
};

export const forceFetchAllMetrics = (
  dashboardConfigs,
  filters,
  dispatch,
  setLastUpdated,
) => {
  if (!filters) return;

  const configs = Array.isArray(dashboardConfigs)
    ? dashboardConfigs
    : [dashboardConfigs];

  const metricsToFetch = [];
  configs.forEach((dashboardConfig) => {
    if (!dashboardConfig || !dashboardConfig.widgets) return;

    dashboardConfig.widgets.forEach((widget) => {
      widget.widgetDefinition.metrics.forEach((metric) => {
        metricsToFetch.push(metric);
      });
    });
  });

  if (metricsToFetch.length > 0) {
    dispatch(fetchMetrics(metricsToFetch, filters));
  }
  setLastUpdated(new Date());
};

export const fetchUnfetchedMetrics = (
  dashboardConfigs,
  filters,
  allMetricData,
  dispatch,
  setLastUpdated,
) => {
  if (!filters) return;

  const configs = Array.isArray(dashboardConfigs)
    ? dashboardConfigs
    : [dashboardConfigs];

  const unfetchedMetrics = [];
  configs.forEach((dashboardConfig) => {
    if (!dashboardConfig || !dashboardConfig.widgets) return;

    dashboardConfig.widgets.forEach((widget) => {
      widget.widgetDefinition.metrics.forEach((metric) => {
        if (!allMetricData[metric.id]) {
          unfetchedMetrics.push(metric);
        }
      });
    });
  });

  if (unfetchedMetrics.length > 0) {
    dispatch(fetchMetrics(unfetchedMetrics, filters));
  }
  setLastUpdated(new Date());
};

/**
 * Build dashboard configuration with priority alerting widgets
 * For priority dashboards, adds alerting widgets from unsaved config to the front,
 * while preventing duplicate widgets to avoid grid layout collisions
 * @param {boolean} isPriorityDashboard - Whether this is a priority dashboard
 * @param {Object} priorityDashboardConfig - Priority dashboard configuration
 * @param {Object} unSavedDashboardConfig - Unsaved dashboard configuration
 * @param {Object} metricData - Metric data state with alert information
 * @param {Array} selectedRoles - Array of selected role values for filtering
 * @returns {Object} Dashboard configuration with merged widgets for priority or original config
 */
export const buildDashboardConfig = (
  isPriorityDashboard,
  priorityDashboardConfig,
  unSavedDashboardConfig,
  metricData,
  selectedRoles = [],
) => {
  if (!isPriorityDashboard) {
    return unSavedDashboardConfig;
  }

  // Helper function to check if widget should be included based on alerts and roles
  const shouldIncludeWidget = (widget) =>
    widget.widgetDefinition.metrics.some((metric) => {
      const metricAlert = metricData[metric.id] && metricData[metric.id].alert;
      return (
        metricAlert &&
        metricAlert.isInAlert &&
        Array.isArray(metricAlert.roles) &&
        metricAlert.roles.some((role) => selectedRoles.includes(role))
      );
    });

  const unsavedFilteredWidgets = (unSavedDashboardConfig.widgets || []).filter(
    shouldIncludeWidget,
  );
  const priorityFilteredWidgets = (
    priorityDashboardConfig.widgets || []
  ).filter(
    (priorityWidget) =>
      !(unSavedDashboardConfig.widgets || []).some(
        (unsavedWidget) =>
          unsavedWidget.widgetId === priorityWidget.widgetId &&
          shouldIncludeWidget(unsavedWidget),
      ),
  );
  return {
    ...priorityDashboardConfig,
    widgets: [...unsavedFilteredWidgets, ...priorityFilteredWidgets],
  };
};

/**
 * Handle scroll-to-focus functionality for newly added widgets
 * @param {string} lastAddedWidgetId - ID of the widget to scroll to
 * @param {Object} layouts - Grid layouts for responsive breakpoints
 * @param {boolean} isPriorityDashboard - Whether this is a priority dashboard
 * @param {Object} widgetRefs - Ref object containing widget DOM references
 * @param {Function} dispatch - Redux dispatch function
 * @returns {Function} Cleanup function for the scroll timeout
 */
export const handleWidgetScrollToFocus = (
  lastAddedWidgetId,
  layouts,
  isPriorityDashboard,
  widgetRefs,
  dispatch,
) => {
  // Edge case 1: Don't scroll for priority dashboard
  if (isPriorityDashboard) {
    return undefined;
  }

  // Edge case 2: Don't scroll if no widget ID to track
  if (!lastAddedWidgetId) {
    return undefined;
  }

  // Edge case 3: Don't scroll if layouts aren't ready
  if (!layouts) {
    return undefined;
  }

  // Edge case 4: Don't scroll if widget ref doesn't exist yet
  if (!widgetRefs.current[lastAddedWidgetId]) {
    return undefined;
  }

  // Debouncing for rapid widget additions - only scroll to most recent
  const scrollTimeout = setTimeout(() => {
    const widgetElement = widgetRefs.current[lastAddedWidgetId];

    // Edge case 5: Double-check element still exists after timeout
    if (widgetElement && lastAddedWidgetId) {
      // Edge case 6: Adjust scroll behavior for mobile layouts
      const isMobileLayout = window.innerWidth <= 480;

      // Smooth scroll to widget instead of instant scroll
      widgetElement.scrollIntoView({
        behavior: "smooth",
        block: isMobileLayout ? "start" : "center",
        inline: "nearest",
      });

      // Add highlight animation class
      widgetElement.classList.add("newly-added-widget");

      // Clear tracking and remove highlight after animation
      const cleanupTimeout = setTimeout(() => {
        dispatch({ type: WIDGET_SCROLL_COMPLETED });
        if (widgetElement) {
          widgetElement.classList.remove("newly-added-widget");
        }
      }, 2000);

      return () => clearTimeout(cleanupTimeout);
    }
  }, 150); // Longer delay to allow sidebar animation and layout changes to complete

  return () => clearTimeout(scrollTimeout);
};



// WEBPACK FOOTER //
// ./src/components/mySCC/common/Dashboard/DashboardUtilities.js